/**
 * Renders the exact same resume preview DOM as the in-app Results step.
 * Used to capture that DOM with html2canvas so the downloaded PDF matches
 * the template view the user sees.
 */

import React, { useRef, useState, useEffect } from 'react';

import type { ResumeSection } from './utils/parseResumeSections';
import { SectionContentWithFormatting } from './utils/sectionContentRenderer';

const A4_PREVIEW_WIDTH = 794;

/**
 * Maps ALL 28 template IDs → their CSS skin class.
 * Must stay in sync with TEMPLATE_LIBRARY in ResultsStep.tsx.
 * Raw skin class strings are also accepted as pass-through for backwards compat.
 */
const TEMPLATE_ID_TO_SKIN: Record<string, string> = {
  // ── All 28 TEMPLATE_LIBRARY IDs ─────────────────────────────────────────
  'ats-optimized':     'blue-classic',
  'bold-dark':         'black-bold',
  'executive':         'black-clean',
  'navy-tech':         'navy-compact',
  'modern-blue':       'blue-modern',
  'developer':         'mono-clean',
  'slate-pro':         'slate-pro',
  'business-clean':    'blue-classic',
  'clean-minimal':     'blue-minimal',
  'creative-bold':     'black-clean',
  'marketing-pro':     'blue-modern',
  'finance-pro':       'navy-compact',
  'accounting':        'blue-classic',
  'healthcare-pro':    'blue-classic',
  'nursing':           'slate-pro',
  'health-admin':      'blue-modern',
  'fresh-grad':        'blue-minimal',
  'junior-tech':       'navy-compact',
  'entry-business':    'blue-classic',
  'classic-serif':     'black-serif',
  'legal-pro':         'black-clean',
  'research-scholar':  'black-serif',
  'faculty':           'mono-clean',
  'academic-clean':    'blue-classic',
  'soft-white':        'soft-white',
  'warm-cream':        'warm-cream',
  'sage-light':        'sage-light',
  'pearl-rose':        'pearl-rose',
  // ── Raw skin class pass-through (legacy / direct skin selection) ─────────
  'blue-classic':  'blue-classic',
  'blue-modern':   'blue-modern',
  'blue-minimal':  'blue-minimal',
  'navy-compact':  'navy-compact',
  'black-bold':    'black-bold',
  'black-clean':   'black-clean',
  'black-serif':   'black-serif',
  'mono-clean':    'mono-clean',
};
export function getTemplateSkinClass(id: string): string {
  return TEMPLATE_ID_TO_SKIN[id] ?? 'blue-classic';
}

const FIRST_SECTION_AS_PERSONAL = new Set([
  'resume', 'full resume', 'profile', 'professional summary', 'summary', 'objective',
  'contact', 'contact information', 'personal information',
]);
function isFirstSectionPersonalInfo(title: string): boolean {
  return FIRST_SECTION_AS_PERSONAL.has(title.toLowerCase().trim());
}

export interface ResumePreviewForPDFProps {
  sections: ResumeSection[];
  templateId: string;
  /** Section header label for the first (contact) section when applicable */
  personalInfoLabel?: string;
}

/**
 * Renders the same structure as ResultsStep's preview so html2canvas
 * produces a PDF that matches the in-app template view.
 */
const ResumePreviewForPDF: React.FC<ResumePreviewForPDFProps> = ({
  sections,
  templateId,
  personalInfoLabel = 'Personal Information',
}) => {
  const skinClass = getTemplateSkinClass(templateId);

  return (
    <div
      className={`resume-preview resume-preview--${skinClass}`}
      style={{ width: 794, maxWidth: 794, background: '#fff', boxSizing: 'border-box' }}
      data-template={templateId}
    >
      <div className="resume-preview-document-inner">
        {sections.map((section, i) => {
          const isContact = i === 0 && isFirstSectionPersonalInfo(section.title);
          const label =
            isContact
              ? personalInfoLabel
              : section.title === 'Full Resume'
                ? 'Full resume'
                : section.title;
          return (
            <div
              key={`section-${i}-${section.title}`}
              className="resume-preview-section-wrapper"
              style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
            >
              {/* Section header + first content line kept together — avoids the
                  dark header bar being orphaned at the bottom of a page */}
              <div
                style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid' }}
              >
                <div
                  className={`resume-preview-section-card overflow-hidden${isContact ? ' resume-preview-contact-card' : ''}`}
                >
                  <div
                    className="resume-preview-section-header"
                    style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid', pageBreakInside: 'avoid', breakInside: 'avoid' }}
                  >
                    <h4 style={{ margin: 0 }}>{label.toUpperCase()}</h4>
                  </div>
                  <div className="resume-preview-section-body">
                    <SectionContentWithFormatting
                      content={section.content || ''}
                      sectionTitle={section.title}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResumePreviewForPDF;
